import axios from './axios';
import { PaginatedResponse } from './types';
import {
  FactoringCreditLineClient,
  FactoringCreditLineDetail,
  FactoringExtractionStatus,
  FactoringHistoryStateFilter,
  FactoringInvoice,
  FactoringInvoicesFilters,
  FactoringProductRequest,
  FactoringCollateralSync,
  FactoringInvoiceStatusFilter,
  FactoringSoldSummary,
  SellFactoringInvoicesPayload,
  SellFactoringInvoicesResponse,
} from '../types/factoringCreditLine';

// Base path of the factoring_credit_line Platform app endpoints.
const factoringBasePath = (companyId: number) => `/companies/${companyId}/products/factoring-credit-line`;

export function getFactoringInvoices(
  companyId: number,
  filters: FactoringInvoicesFilters
): Promise<PaginatedResponse<FactoringInvoice>> {
  const params = new URLSearchParams();
  params.append('page', `${filters.page + 1}`);
  params.append('page_size', `${filters.pageSize}`);
  params.append('eligibility', filters.eligibility);
  if (filters.search) {
    params.append('search', filters.search);
  }
  // History screen: server-side restrict to bought invoices (and optionally active/expired).
  if (filters.scope) {
    params.append('scope', filters.scope);
  }
  if (filters.state && filters.state !== FactoringHistoryStateFilter.ALL) {
    params.append('state', filters.state);
  }
  if (filters.status && filters.status !== FactoringInvoiceStatusFilter.ALL) {
    params.append('status', filters.status);
  }
  return axios.get(`${factoringBasePath(companyId)}/invoices/?${params.toString()}`).then(({ data }) => data);
}

export function getExtractionStatus(companyId: number): Promise<FactoringExtractionStatus> {
  return axios.get(`${factoringBasePath(companyId)}/extraction-status/`).then(({ data }) => data);
}

export function syncFactoringCollateral(companyId: number): Promise<FactoringCollateralSync> {
  return axios.post(`${factoringBasePath(companyId)}/collateral/sync/`).then(({ data }) => data);
}

export function sellInvoices(
  companyId: number,
  payload: SellFactoringInvoicesPayload
): Promise<SellFactoringInvoicesResponse> {
  return axios.post(`${factoringBasePath(companyId)}/sell/`, payload).then(({ data }) => data);
}

export function refreshInvoices(companyId: number, automatic = false): Promise<void> {
  return axios
    .post(`${factoringBasePath(companyId)}/invoices/refresh/`, { automatic })
    .then(({ data }) => data);
}

export const exportFactoringInvoices = async (
  companyId: number,
  filters?: Partial<Pick<FactoringInvoicesFilters, 'search' | 'eligibility' | 'scope' | 'state' | 'status'>>
): Promise<Blob> => {
  // Keep the export in sync with the on-screen list (history scope, status and search).
  const params = new URLSearchParams();
  if (filters?.eligibility) {
    params.append('eligibility', filters.eligibility);
  }
  if (filters?.search) {
    params.append('search', filters.search);
  }
  if (filters?.scope) {
    params.append('scope', filters.scope);
  }
  if (filters?.state && filters.state !== FactoringHistoryStateFilter.ALL) {
    params.append('state', filters.state);
  }
  if (filters?.status && filters.status !== FactoringInvoiceStatusFilter.ALL) {
    params.append('status', filters.status);
  }
  const query = params.toString();
  const response = await axios.get(
    `${factoringBasePath(companyId)}/invoices/export/${query ? `?${query}` : ''}`,
    { responseType: 'blob' }
  );
  return response.data;
};

export function getFactoringSoldSummary(companyId: number): Promise<FactoringSoldSummary> {
  return axios.get(`${factoringBasePath(companyId)}/sold-summary/`).then(({ data }) => data);
}

// Factoring credit lines live in RCL: reuse the existing RCL client endpoints (plan §2.5).
export function getFactoringCreditLineList(
  companyId: number
): Promise<PaginatedResponse<FactoringCreditLineClient>> {
  return axios.get(`/companies/${companyId}/products/rcl/credit-lines/`).then(({ data }) => data);
}

export function getFactoringCreditLineDetail(
  companyId: number,
  creditLineId: string
): Promise<FactoringCreditLineDetail> {
  return axios
    .get(`/companies/${companyId}/products/rcl/credit-lines/${creditLineId}/`)
    .then(({ data }) => data);
}

// Product enablement request: the client asks Marco to enable the factoring credit line.
// Unlike the revolving line, factoring has no requested amount (availability comes from
// selling invoices), so the request body is empty.
export function getFactoringProductRequests(companyId: number): Promise<FactoringProductRequest[]> {
  return axios.get(`${factoringBasePath(companyId)}/product-requests/`).then(({ data }) => data);
}

export function submitFactoringProductRequest(
  companyId: number,
  requestedAmount: number
): Promise<FactoringProductRequest> {
  return axios
    .post(`${factoringBasePath(companyId)}/product-requests/`, { requestedAmount })
    .then(({ data }) => data);
}
